/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable unused-imports/no-unused-vars */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  IconButton,
  FormControl,
  MenuItem,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Save } from '@/components/icons';
import Typography from '@/components/typography/typography';
import { Ic } from '../engine/icons';

function SimpleColumnsEditor({ tableData, setTableData, placeholders }: { tableData: any, setTableData: any, placeholders: any[] }) {
  const patch = (i: number, p: any) => {
    const newCols = (tableData.columns || []).map((c: any, j: number) => j === i ? { ...c, ...p } : c);
    setTableData({ ...tableData, columns: newCols });
  };
  const move = (i: number, d: number) => {
    const newCols = [...tableData.columns];
    const j = i + d;
    if (j < 0 || j >= newCols.length) return;
    [newCols[i], newCols[j]] = [newCols[j], newCols[i]];
    setTableData({ ...tableData, columns: newCols });
  };
  const remove = (i: number) => {
    const newCols = tableData.columns.filter((_: any, j: number) => j !== i);
    setTableData({ ...tableData, columns: newCols });
  };
  const add = () => {
    const newId = 'col' + Math.random().toString(36).slice(2, 8);
    setTableData({ ...tableData, columns: [...(tableData.columns || []), { id: newId, header: 'New column', align: 'l' }] });
  };

  const bind = (i: number, val: string) => {
    const colId = tableData.columns[i].id;
    const newRows = [...(tableData.rows || [])];
    if (newRows.length === 0) newRows.push({});
    newRows[0] = { ...newRows[0], [colId]: val };
    setTableData({ ...tableData, rows: newRows });
  };

  const setAlign = (i: number, alignVal: string) => {
    patch(i, { align: alignVal });
  };

  const opts = [...placeholders.map(p => ({ v: p.value, label: p.label })), { v: '__custom', label: 'Custom field…' }];

  return (
    <div className="cx-cols">
      {tableData.columns?.map((col: any, i: number) => {
        const boundVal = tableData.rows?.[0]?.[col.id] || '';
        const alignVal = col.align || 'l';
        return (
          <div className="cx-col" key={col.id}>
            <div className="cx-col-top">
              <span className="cx-grip"><Ic k="grip" /></span>
              <input
                className="cx-input cx-col-lbl"
                value={col.header || ''}
                onChange={e => patch(i, { header: e.target.value })}
                placeholder="Column header"
              />
              <div className="cx-item-move">
                <button onClick={() => move(i, -1)} disabled={i === 0} title="Move left"><Ic k="arrowUp" /></button>
                <button onClick={() => move(i, 1)} disabled={i === (tableData.columns || []).length - 1} title="Move right"><Ic k="arrowDn" /></button>
                <button className="cx-item-del" onClick={() => remove(i)} disabled={(tableData.columns || []).length <= 1} title="Remove column"><Ic k="trash" /></button>
              </div>
            </div>
            <div className="cx-col-bind">
              <select
                className="cx-select"
                value={boundVal}
                onChange={e => bind(i, e.target.value)}
              >
                <option value="">Select data source...</option>
                {opts.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
              <div className="cx-seg ">
                <button className={alignVal === 'l' ? 'is-active' : ''} onClick={() => setAlign(i, 'l')} title="L">L</button>
                <button className={alignVal === 'r' ? 'is-active' : ''} onClick={() => setAlign(i, 'r')} title="R">R</button>
              </div>
            </div>
          </div>
        );
      })}
      <button className="cx-add" onClick={add}><Ic k="plus" />Add column</button>
    </div>
  );
}

// Import sub-components
import TextControls from './controllers/TextControls';
import ButtonControls from './controllers/ButtonControls';
import ImageControls from './controllers/ImageControls';
import DividerControls from './controllers/DividerControls';
import TableControls from './controllers/TableControls';
import SignatureControls from './controllers/SignatureControls';
import CodeControls from './controllers/CodeControls';
import Select from '@/components/select/select';
import { EnhancedSectionData, ElementType } from '../types/editor';
import { initialStyles } from '../utils/constant';
import { extractStyles, generateInlineStyles, getInitialSectionData, applyOpacityToColor } from '../utils/common';
// import { useCKEditorStyleExtractor } from '../utils/style-extractor';

// Shared types for SectionFormatPanel and sub-components
export interface TableColumn {
  id: string;
  header: string;
  placeholder?: string;
}

export interface TableRow {
  [key: string]: string | number | boolean;
}

export interface CellMerge {
  section: 'thead' | 'tbody';
  rowStart: number;
  rowEnd: number;
  colStart: number;
  colEnd: number;
  value: string;
}

export interface TableData {
  name?: string; // Added name property for #each iteration
  columns: TableColumn[];
  rows: TableRow[];
  border: boolean;
  mergedCells?: CellMerge[];
}

interface SectionData {
  type: 'text' | 'text-block' | 'heading' | 'heading-2' | 'heading-3' | 'heading-4' | 'buttons' | 'image' | 'divider' | 'table' | 'signature' | 'qr-code' | 'bar-code';
  content: string;
  attributes?: {
    styles?: any;
    placeholders?: Record<string, string>;
    tableData?: TableData;
    [key: string]: any;
  };
}

interface SectionFormatPanelProps {
  section: SectionData | undefined;
  onClose: () => void;
  onUpdate: (data: Partial<EnhancedSectionData>) => void;
  placeholders: Record<string, any>[];
  handleSave?: () => void;
  isEmbedded?: boolean;
  hidePresetSelector?: boolean;
}

const sectionTypes = ['text', 'text-block', 'heading', 'heading-2', 'heading-3', 'heading-4', 'buttons', 'image', 'divider', 'table', 'signature', 'qr-code', 'bar-code']

const SectionFormatPanel: React.FC<SectionFormatPanelProps> = ({
  section,
  onClose,
  onUpdate,
  placeholders,
  handleSave,
  isEmbedded = false,
  hidePresetSelector = false
}) => {
  // const { applyStylesToHtml } = useCKEditorStyleExtractor()
  // const [plainContent, setPlainContent] = useState('');
  const sectionType = useMemo(() => section?.type || 'text', [section?.type]);
  const content = useMemo(() => section?.content || 'text', [section?.content]);
  const [formatOptions, setFormatOptions] = useState<string[]>([]);
  const [rowLevel, setRowLevel] = useState<boolean>((section?.attributes as any)?.rowLevel || false);
  const [tableData, setTableData] = useState<TableData>({
    name: '', // Initialize with empty name
    columns: [
      { id: 'col1', header: 'Column 1' },
      { id: 'col2', header: 'Column 2' },
      { id: 'col3', header: 'Column 3' }
    ],
    rows: [
      { col1: '', col2: '', col3: '' }
    ],
    border: true,
    mergedCells: []
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedContentAttrObj, setSelectedContentAttrObj] = useState(extractStyles(content, section?.type));

  useEffect(() => {
    if (section && section.type === 'table') {
      // If section has stored table data, use it
      if (section.attributes?.tableData) {
        setTableData(section.attributes.tableData);
      } else {
        // Try to extract table data from HTML content
        const extractedTableData = extractTableDataFromHTML(section.content);
        if (extractedTableData) {
          setTableData(extractedTableData);
          setRowLevel((section?.attributes as any)?.rowLevel || false);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (section && !isInitialLoad) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = section.content;
      const text = tempDiv.textContent || '';
      // setPlainContent(text);
      setSelectedContentAttrObj(extractStyles(section.content, section?.type)?.styles)
      setIsInitialLoad(false);
    }
  }, [section]);

  // Extract table data from HTML content
  const extractTableDataFromHTML = (htmlContent: string): TableData | null => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const table = doc.querySelector('table');

      if (!table) return null;

      const headerRows = table.querySelectorAll('thead tr');
      const bodyRows = table.querySelectorAll('tbody tr');

      if (headerRows.length === 0) return null;

      // Extract columns from the first header row
      const headerCells = headerRows[0].querySelectorAll('th');
      const columns: TableColumn[] = Array.from(headerCells).map((th, index) => ({
        id: `col${index + 1}`,
        header: th.textContent?.trim() || `Column ${index + 1}`
      }));

      // Extract merged cells from thead
      const mergedCells: CellMerge[] = [];
      Array.from(headerRows).forEach((row, rowIndex) => {
        const cells = row.querySelectorAll('th');
        cells.forEach((cell, colIndex) => {
          const rowSpan = parseInt(cell.getAttribute('rowspan') || '1');
          const colSpan = parseInt(cell.getAttribute('colspan') || '1');
          if (rowSpan > 1 || colSpan > 1) {
            mergedCells.push({
              section: 'thead',
              rowStart: rowIndex,
              rowEnd: rowIndex + rowSpan - 1,
              colStart: colIndex,
              colEnd: colIndex + colSpan - 1,
              value: cell.textContent?.trim() || ''
            });
          }
        });
      });

      // Extract rows
      const rows: TableRow[] = Array.from(bodyRows).map(row => {
        const cells = row.querySelectorAll('td');
        const rowData: TableRow = {};

        cells.forEach((cell, index) => {
          if (index < columns.length) {
            rowData[columns[index].id] = cell.textContent?.trim() || '';
          }
        });

        return rowData;
      });

      // Extract merged cells from tbody
      Array.from(bodyRows).forEach((row, rowIndex) => {
        const cells = row.querySelectorAll('td');
        cells.forEach((cell, colIndex) => {
          const rowSpan = parseInt(cell.getAttribute('rowspan') || '1');
          const colSpan = parseInt(cell.getAttribute('colspan') || '1');
          if (rowSpan > 1 || colSpan > 1) {
            mergedCells.push({
              section: 'tbody',
              rowStart: rowIndex,
              rowEnd: rowIndex + rowSpan - 1,
              colStart: colIndex,
              colEnd: colIndex + colSpan - 1,
              value: cell.textContent?.trim() || ''
            });
          }
        });
      });

      return {
        name: '', // Default empty name
        columns,
        rows: rows.length > 0 ? rows : [{}],
        border: true,
        mergedCells
      };
    } catch (error) {
      return null;
    }
  };

  // Get placeholder value for table generation
  const getPlaceholderValue = (placeholderKey: string): string => {
    const placeholder = placeholders.find(p => p.value === placeholderKey || p.id === placeholderKey || p.field === placeholderKey);
    if (!placeholder) return `{{${placeholderKey}}}`;

    // Return actual value or generate sample based on type
    if (typeof placeholder.actualValue !== 'undefined') {
      return String(placeholder.actualValue);
    }

    switch (placeholder.type) {
      case 'date':
        return new Date().toLocaleDateString();
      case 'datetime':
        return new Date().toLocaleString();
      case 'number':
        return '123';
      case 'currency':
        return '$1,234.56';
      case 'email':
        return 'example@domain.com';
      case 'phone':
        return '+1 (555) 123-4567';
      case 'url':
        return 'https://example.com';
      default:
        const key = placeholder.value || placeholder.id || placeholder.field;
        return `{{${key}}}` || placeholder.label;
    }
  };

  const generateContent = useCallback((attrs?: Record<any, any>): string => {
    const { src, alt, styles: eStyles, text, placeholder, placeholders: tPlaceholders } = attrs || section?.attributes || selectedContentAttrObj || {}
    const styles = {
      ...initialStyles[sectionType],
      ...eStyles,
      ...selectedContentAttrObj?.styles,
      ...attrs?.styles
    }

    const inlineStyles = generateInlineStyles(styles);

    // For buttons, images, dividers, and codes - use the existing logic
    if (sectionType === 'buttons') {
      return `<a href="${attrs?.href || '#'}" target="_blank" style="${inlineStyles}; display: flex !important; text-decoration: none !important; box-sizing: border-box !important; justify-content: center !important; align-items: center !important; width: 100% !important; height: 100% !important; text-align: center !important; word-break: break-word !important; white-space: normal !important;">${text || 'Button text'}</a>`;
    } else if (sectionType === 'image') {
      let imgInlineStyles: string = inlineStyles;
      if (styles?.is_bordered) {
        styles.padding = '10px';
        imgInlineStyles = generateInlineStyles(styles);
      } else {
        styles.padding = '0';
        imgInlineStyles = generateInlineStyles(styles);
      }
      return `<img src="${src || ''}" alt="${alt || ''}" style="${imgInlineStyles}" />`;
    } else if (sectionType === 'divider') {
      return `<hr style="${inlineStyles}" />`;
    } else if (sectionType === 'table') {
      const { border, borderStyle, borderColor, height, opacity, ...rest } = styles;
      const tableOpacity = typeof opacity === 'number' ? opacity : 1;
      const filteredStyles = { ...(styles.is_bordered ? styles : rest) };
      delete filteredStyles.height;
      delete (filteredStyles as any).opacity;
      const tInlineStyles = generateInlineStyles(filteredStyles);
      const tHeadRowHeight = Number(styles.tHead?.rowHeight) || 38;
      const tBodyRowHeight = Number(styles.tBody?.rowHeight) || 38;

      const rawHeadBg = styles.tHead?.backgroundColor || 'rgba(245, 245, 245, 0.75)';
      const headBgColor = applyOpacityToColor(rawHeadBg, tableOpacity);

      const hasZebra = !!styles.tBody?.zebraStriping;
      const rawAltColor = styles.tBody?.alternateRowColor || '#f9f9f9';
      const altColor = applyOpacityToColor(rawAltColor, tableOpacity);

      const rawMainBg = styles.tBody?.backgroundColor || '#ffffff';
      const mainBgColor = applyOpacityToColor(rawMainBg, tableOpacity);

      const thUserFontSize = Number(styles.tHead?.fontSize) || 14;
      const thBasePadding = typeof styles.thCells?.padding === 'number' ? styles.thCells.padding : 8;
      const thAvailH = Math.max(4, tHeadRowHeight - 1);
      const effectiveThFontSize = Math.min(thUserFontSize, Math.max(8, thAvailH - 2));
      const thRemainingV = Math.max(0, thAvailH - effectiveThFontSize);
      const thVPadding = Math.max(0, Math.min(thBasePadding, Math.floor(thRemainingV / 2)));
      const thHPadding = thBasePadding;
      const thWhiteSpace = tHeadRowHeight < 36 ? 'nowrap' : 'normal';

      const tbUserFontSize = Number(styles.tBody?.fontSize) || 14;
      const tbBasePadding = typeof styles.tbCells?.padding === 'number' ? styles.tbCells.padding : 8;
      const tbAvailH = Math.max(4, tBodyRowHeight - 1);
      const effectiveTbFontSize = Math.min(tbUserFontSize, Math.max(8, tbAvailH - 2));
      const tbRemainingV = Math.max(0, tbAvailH - effectiveTbFontSize);
      const tbVPadding = Math.max(0, Math.min(tbBasePadding, Math.floor(tbRemainingV / 2)));
      const tbHPadding = tbBasePadding;
      const tbWhiteSpace = tBodyRowHeight < 36 ? 'nowrap' : 'normal';

      const { padding: _thPad, ...thCellsRest } = styles.thCells || {};
      const { padding: _tbPad, ...tbCellsRest } = styles.tbCells || {};

      const { rowHeight: _thRH, backgroundColor: _thBg, ...tHeadRest } = styles.tHead || {};
      const { rowHeight: _tbRH, backgroundColor: _tbBg, ...tBodyRest } = styles.tBody || {};

      const tHeadInlineStyles: string = generateInlineStyles(tHeadRest);
      const tBodyInlineStyles: string = generateInlineStyles(tBodyRest);
      const thCellInlineStyles: string = generateInlineStyles({
        ...tHeadRest,
        ...thCellsRest,
        ...(styles.is_bordered ? { border, borderStyle, borderColor } : {})
      });
      const { backgroundColor: _tbBg2, ...tBodyRestNoBg } = tBodyRest;
      const tbCellInlineStyles: string = generateInlineStyles({
        ...tBodyRestNoBg,
        ...tbCellsRest,
        ...(styles.is_bordered ? { border, borderStyle, borderColor } : {})
      });

      // Helper function to check if a cell is part of a merge
      const getCellMergeInfo = (section: 'thead' | 'tbody', rowIndex: number, colIndex: number) => {
        return tableData.mergedCells?.find(m =>
          m.section === section &&
          rowIndex >= m.rowStart && rowIndex <= m.rowEnd &&
          colIndex >= m.colStart && colIndex <= m.colEnd
        );
      };

      // Generate header row with merge support
      const headerCellsHTML = (tableData.columns || []).map((column, colIndex) => {
        const mergeInfo = getCellMergeInfo('thead', 0, colIndex);

        if (mergeInfo && (colIndex !== mergeInfo.colStart)) {
          return '';
        }

        const mergeAttributes = mergeInfo ?
          ` rowspan="${mergeInfo.rowEnd - mergeInfo.rowStart + 1}" colspan="${mergeInfo.colEnd - mergeInfo.colStart + 1}"` : '';

        const cellValue = mergeInfo ? mergeInfo.value : column.header;
        const alignStyle = column.align === 'r' ? 'text-align: right;' : column.align === 'l' ? 'text-align: left;' : '';

        return `<th style="${thCellInlineStyles}${thCellInlineStyles.endsWith(';') ? '' : ';'}${alignStyle}; vertical-align: middle; height: ${tHeadRowHeight}px; max-height: ${tHeadRowHeight}px; box-sizing: border-box; padding: ${thVPadding}px ${thHPadding}px !important; font-size: ${effectiveThFontSize}px !important; line-height: 1 !important; overflow: hidden; text-overflow: ellipsis; white-space: ${thWhiteSpace}; background-color: ${headBgColor} !important;"${mergeAttributes}>${cellValue}</th>`;
      }).filter(cell => cell !== '').join('');

      // Generate table body rows
      const rowsToRender = (tableData?.rows && tableData.rows.length > 0) ? tableData.rows : [{}];
      const tableRowsHTML = rowsToRender.map((row, rowIndex) => {
        const rowBgColor = hasZebra && rowIndex % 2 === 1 ? altColor : mainBgColor;
        const rowStyleAddition = `; background-color: ${rowBgColor} !important;`;

        const cellsHTML = (tableData.columns || []).map((column, colIndex) => {
          const mergeInfo = getCellMergeInfo('tbody', rowIndex, colIndex);

          if (mergeInfo && (rowIndex !== mergeInfo.rowStart || colIndex !== mergeInfo.colStart)) {
            return '';
          }

          let cellValue = row[column.id] || '';
          const keys = Object.keys(row || {});
          if (keys.includes(`${column.id}_static`)) {
            cellValue = row[`${column.id}_static`] || '';
          }
          if (!cellValue && tPlaceholders?.[column.id]) {
            cellValue = getPlaceholderValue(tPlaceholders[column.id]);
          }

          const mergeAttributes = mergeInfo ?
            ` rowspan="${mergeInfo.rowEnd - mergeInfo.rowStart + 1}" colspan="${mergeInfo.colEnd - mergeInfo.colStart + 1}"` : '';

          let finalCellValue = mergeInfo ? mergeInfo.value : cellValue;
          if (typeof finalCellValue === 'string' && finalCellValue.includes('{{') && finalCellValue.includes('}}')) {
            finalCellValue = finalCellValue.replace(/\{\{([^}]+)\}\}/g, '<span class="cx-ph-tag">{{$1}}</span>');
          }
          const alignStyle = column.align === 'r' ? 'text-align: right;' : column.align === 'l' ? 'text-align: left;' : '';

          return `<td style="${tbCellInlineStyles}${tbCellInlineStyles.endsWith(';') ? '' : ';'}${alignStyle}; vertical-align: middle; height: ${tBodyRowHeight}px; max-height: ${tBodyRowHeight}px; box-sizing: border-box; padding: ${tbVPadding}px ${tbHPadding}px !important; font-size: ${effectiveTbFontSize}px !important; line-height: 1 !important; overflow: hidden; text-overflow: ellipsis; white-space: ${tbWhiteSpace}; background-color: ${rowBgColor} !important;${rowStyleAddition}"${mergeAttributes}>${finalCellValue}</td>`;
        }).filter(cell => cell !== '').join('');

        return `<tr style="${tBodyInlineStyles}; height: ${tBodyRowHeight}px; max-height: ${tBodyRowHeight}px; line-height: 1;${rowStyleAddition}">${cellsHTML}</tr>`;
      }).join('');

      return `<table style="${tInlineStyles}; width: 100%; border-collapse: collapse; height: auto !important; table-layout: fixed; margin: 0 !important; background-color: transparent !important;"><thead><tr style="${tHeadInlineStyles}; background-color: ${headBgColor} !important; height: ${tHeadRowHeight}px; max-height: ${tHeadRowHeight}px; line-height: 1;">${headerCellsHTML}</tr></thead><tbody>${tableRowsHTML}</tbody></table>`.trim();
    } else if (sectionType === 'qr-code' || sectionType === 'bar-code') {
      return `<div style="${inlineStyles}">${placeholder}</div>`;
    } else if (sectionType === 'signature') {
      const signatureText = (attrs || section?.attributes)?.signatureText || section?.attributes?.signatureText || 'Signature';
      return `<svg viewBox="0 0 300 80" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg"><text x="50%" y="55%" dominant-baseline="middle" text-anchor="middle" fill="${(attrs || section?.attributes)?.styles?.color || '#333333'}" style="${inlineStyles}">${signatureText}</text></svg>`;
    }

    // For text-based sections, parse and apply styles to existing HTML tags
    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(content);

    if (!hasHtmlTags) {
      // If no HTML tags, wrap according to section type
      switch (sectionType) {
        case 'text':
        case 'text-block':
          return `<p style="${inlineStyles}">${content}</p>`;
        case 'heading':
          return `<h1 style="${inlineStyles}">${content}</h1>`;
        case 'heading-2':
          return `<h2 style="${inlineStyles}">${content}</h2>`;
        case 'heading-3':
          return `<h3 style="${inlineStyles}">${content}</h3>`;
        case 'heading-4':
          return `<h4 style="${inlineStyles}">${content}</h4>`;
        case 'signature':
          return `<svg viewBox="0 0 300 60" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="currentColor" style="${inlineStyles}">${content}</text></svg>`;
        default:
          return `<p style="${inlineStyles}">${content}</p>`;
      }
    } else {
      // Create a temporary DOM element to manipulate the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${content}</div>`, 'text/html');
      const container = doc.body.firstChild as Element;

      // Process all child elements
      if (container && container.nodeType === Node.ELEMENT_NODE) {
        const elements = container.querySelectorAll('*');

        elements.forEach(element => {
          // Skip processing script tags or other special elements
          if (element.tagName === 'SCRIPT' || element.tagName === 'STYLE') {
            return;
          }

          // Get existing style and merge with new style
          const existingStyle = element.getAttribute('style') || '';

          // Extract properties from existing style
          const existingProps = {};
          existingStyle.split(';').forEach(prop => {
            const [key, value] = prop.split(':').map(item => item.trim());
            if (key && value) {
              existingProps[key] = value;
            }
          });

          // Parse the base style and prepare for merging
          const newStyleProps = {};
          inlineStyles.split(';').forEach(prop => {
            const [key, value] = prop.split(':').map(item => item.trim());
            if (key && value) {
              newStyleProps[key] = value;
            }
          });

          // Additional styles based on tag type
          if (element.tagName === 'P' || element.tagName === 'DIV') {
            newStyleProps['line-height'] = '1.5';
          } else if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(element.tagName)) {
            newStyleProps['line-height'] = '1.2';
            newStyleProps['margin'] = '0.5em 0';
          }

          // Merge styles, new styles override existing ones
          const mergedProps = { ...existingProps, ...newStyleProps };

          // Convert back to style string
          const mergedStyle = Object.entries(mergedProps)
            .filter(([key, value]) => key && value)
            .map(([key, value]) => `${key}: ${value}`)
            .join('; ');

          element.setAttribute('style', mergedStyle);
        });

        return container.innerHTML;
      }

      // Fallback to original content if parsing fails
      return content;
    }
  }, [JSON.stringify(section)]);

  // Local save function - renamed to avoid conflict with handleSave prop
  const saveSection = () => {
    const updatedContent = generateContent();

    const updatedSection = {
      content: updatedContent,
      type: sectionType as ElementType,
      rowLevel: rowLevel,
      name: tableData.name || '',
      attributes: {
        ...(section?.attributes || {}),
        ...(sectionType === 'table' ? { tableData } : {})
      },
      is_edited_from_source: (section as EnhancedSectionData)?.is_edited_from_source || false
    };

    onUpdate(updatedSection);
  };
  useEffect(() => {
    saveSection();
  }, [tableData])
  /**
   * Updates section attributes and triggers content regeneration.
   * Optimized to avoid unnecessary property spreading and glitches.
   */
  const updateAttrs = (prop: Record<string, any>) => {
    if (!section) return;

    // Merge styles safely
    const mergedStyles = {
      ...(section.attributes?.styles || {}),
      ...(prop.styles || {})
    };

    // Merge attributes, giving priority to new props
    const mergedAttributes = {
      ...(section.attributes || {}),
      ...prop,
      styles: mergedStyles,
      ...(sectionType === 'table' ? { tableData } : {})
    };

    // Prepare updated section object (exclude 'id' if present)
    const { id, ...sectionRest } = section as any;

    // Generate updated content based on new attributes
    const updatedContent = generateContent(mergedAttributes);
    setSelectedContentAttrObj(mergedAttributes)

    // Sync image/buttons/table dimension updates from sidebar to the canvas wrapper
    const dimensionUpdates: any = {};
    if (sectionType === 'image' || sectionType === 'buttons') {
      if (typeof mergedAttributes.styles?.height === 'number') {
        dimensionUpdates.height = mergedAttributes.styles.height;
      }

      if (mergedAttributes.styles?.fullWidth && sectionType === 'image') {
        dimensionUpdates.width = 794;
        dimensionUpdates.x = 0;
      } else if (typeof mergedAttributes.styles?.width === 'number') {
        dimensionUpdates.width = mergedAttributes.styles.width;
      }
    } else if (sectionType === 'table') {
      const tHeadH = Number(mergedAttributes.styles?.tHead?.rowHeight) || 38;
      const tBodyH = Number(mergedAttributes.styles?.tBody?.rowHeight) || 38;
      const numRows = (tableData?.rows && tableData.rows.length > 0) ? tableData.rows.length : 1;
      const calculatedH = tHeadH + (numRows * tBodyH) + 2;
      dimensionUpdates.height = Math.max(section?.height || 0, calculatedH);
      if (typeof mergedAttributes.styles?.width === 'number') {
        dimensionUpdates.width = mergedAttributes.styles.width;
      }
    }

    // Compose the update payload, omitting unknown properties
    onUpdate({
      ...sectionRest,
      ...dimensionUpdates,
      content: updatedContent,
      attributes: mergedAttributes
    });
  };

  // Update table data and trigger content regeneration
  const handleTableDataUpdate = (newTableData: TableData) => {
    setTableData(newTableData);

    // Trigger immediate update with new table data
    const updatedAttrs = {
      ...(section?.attributes || {}),
      tableData: newTableData
    };

    // const updatedContent = generateContent(updatedAttrs);
    // onUpdate({
    //   ...section,
    //   content: updatedContent,
    //   attributes: updatedAttrs
    // });
    saveSection();
  };

  const handleTypeChange = (t) => {
    const dt = getInitialSectionData(t)

    onUpdate({
      ...section,
      ...dt
    });

    // Reset table data when switching types
    if (t === 'table') {
      setTableData({
        name: '', // Initialize with empty name
        columns: [
          { id: 'col1', header: 'Column 1' },
          { id: 'col2', header: 'Column 2' },
          { id: 'col3', header: 'Column 3' }
        ],
        rows: [
          { col1: '', col2: '', col3: '' }
        ],
        border: true,
        mergedCells: []
      });
    }
  }

  return (
    <Paper
      elevation={isEmbedded ? 0 : 3}
      sx={{
        width: isEmbedded ? '100%' : { xs: '100%', sm: 320, lg: 390 },
        height: '100%',
        overflow: 'auto',
        borderLeft: isEmbedded ? 'none' : '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        boxShadow: 'none',
      }}
    >
      <Box
        sx={{
          display: isEmbedded ? 'none' : 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Typography type="s1" weight='bold' color='theme.neutral.1000'>
          Format {sectionType ? sectionType?.charAt(0)?.toUpperCase() + sectionType?.slice(1).replace(/-/g, ' ') : ''}
        </Typography>
        <Tooltip title="Save and Close">
          <Box>
            <IconButton size="small" onClick={() => {
              saveSection();
              handleSave?.();
              onClose();
            }} aria-label="Save">
              <Save fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
      </Box>

      <Box sx={{ p: 2, flex: 1, pb: 10, overflowY: 'auto' }}>
        {!hidePresetSelector && (
          <Box sx={{ mb: 2 }}>
            <FormControl size="small" fullWidth>
              <Select
                value={section?.type}
                onChange={(e) => handleTypeChange(e.target.value)}
                renderValue={v => v ? v.charAt(0).toUpperCase() + v.slice(1).replace(/-/g, ' ') : ''}
                label='Select Preset'
                displayEmpty
              >
                {sectionTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    <Typography type='s4' color='theme.secondary.1000'>{type.charAt(0).toUpperCase() + type.slice(1).replace(/-/g, ' ')}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {section && (sectionType === 'text' ||
          sectionType === 'heading' ||
          sectionType === 'heading-2' ||
          sectionType === 'heading-3' ||
          sectionType === 'heading-4') && (
            <TextControls
              attrs={section?.attributes || {}}
              updateAttrs={updateAttrs}
              placeholders={placeholders || []}
              content={section.content || ''}
              updateContent={(content) => onUpdate({ ...section, content })}
            />
          )}
        {sectionType === 'buttons' && (
          <ButtonControls
            attrs={{
              ...(section?.attributes || {}),
              styles: {
                ...(section?.attributes?.styles || {}),
                width: (section as any)?.width ?? section?.attributes?.styles?.width ?? 130,
                height: (section as any)?.height ?? section?.attributes?.styles?.height ?? 40,
              }
            }}
            updateAttrs={updateAttrs}
          />
        )}
        {sectionType === 'image' && (
          <ImageControls
            attrs={{
              ...(section?.attributes || {}),
              styles: {
                ...(section?.attributes?.styles || {}),
                width: (section as any)?.width ?? section?.attributes?.styles?.width ?? 200,
                height: (section as any)?.height ?? section?.attributes?.styles?.height ?? 150,
              }
            }}
            updateAttrs={updateAttrs}
          />
        )}
        {sectionType === 'divider' && (
          <DividerControls
            attrs={section?.attributes || {}}
            updateAttrs={updateAttrs}
          />
        )}
        {sectionType === 'table' && (
          <TableControls
            key={section?.id}
            tableData={{
              ...tableData,
              columns: tableData?.columns || [],
              rows: (tableData?.rows || []).map(row => {
                const convertedRow: Record<string, string> = {};
                Object.keys(row || {}).forEach(key => {
                  convertedRow[key] = String(row[key] ?? '');
                });
                return convertedRow;
              })
            }}
            setTableData={(value: any) => {
              const newValue = typeof value === 'function' ? value(tableData) : value;
              handleTableDataUpdate({
                ...newValue,
                border: tableData.border,
                name: ''
              });
            }}
            attrs={section?.attributes || {}}
            updateAttrs={updateAttrs}
            placeholders={placeholders}
            handleSave={handleSave}
            setParentRowLevel={setRowLevel}
            levelType={rowLevel}
          />
        )}
        {sectionType === 'signature' && (
          <SignatureControls
            attrs={section?.attributes || {}}
            updateAttrs={updateAttrs}
          />
        )}
        {section && (sectionType === 'qr-code' || sectionType === 'bar-code') && (
          <CodeControls
            attrs={section?.attributes || {}}
            updateAttrs={updateAttrs}
          />
        )}
      </Box>
    </Paper>
  );
};

export default SectionFormatPanel;